// SyncEngine (ARCHITECTURE 7.2): анонимное устройство, отправка журнала пачками, приём по серверному курсору.
import { Api, ApiError, OfflineError } from "./api.ts";
import type { BrovkaDB, Child, Stage, StoredEvent } from "./db.ts";

const BATCH = 500;

type ServerEvent = {
  server_seq: number;
  event_id: string;
  child_id: string;
  device_id: string;
  type: StoredEvent["type"];
  occurred_at: string;
  content_release?: string | null;
  payload: Record<string, unknown>;
};

type SyncResp = {
  accepted: string[];
  rejected: { event_id: string; code: string }[];
  events: ServerEvent[];
  has_more: boolean;
  cursor: number;
};

export type SyncResult = { sent: number; received: number; offline: boolean };

export class SyncEngine {
  private running: Promise<SyncResult> | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly api: Api,
    private readonly db: BrovkaDB,
    private readonly onChange: () => void = () => {},
  ) {}

  /** Синхронизировать сейчас (повторные вызовы объединяются). */
  syncNow(): Promise<SyncResult> {
    if (!this.running) {
      this.running = this.run().finally(() => {
        this.running = null;
      });
    }
    return this.running;
  }

  private async run(): Promise<SyncResult> {
    let sent = 0;
    let received = 0;
    try {
      await this.api.ensureDevice();
      const deviceId = await this.api.deviceId();
      for (let guard = 0; guard < 50; guard++) {
        const pending = await this.db.events.where("state").equals(0).limit(BATCH).toArray();
        const cursor = (await this.db.get<number>("syncCursor")) ?? 0;
        const r = await this.api.request<SyncResp>("POST", "/v1/sync", {
          device_id: deviceId,
          cursor,
          events: pending.map((e) => ({ event_id: e.eventId, child_id: e.childId, type: e.type, occurred_at: e.occurredAt, content_release: e.contentRelease, payload: e.payload })),
        });
        const accepted = new Set(r.accepted);
        const rejected = new Map(r.rejected.map((x) => [x.event_id, x.code]));
        await this.db.transaction("rw", this.db.events, this.db.children, this.db.kv, async () => {
          for (const e of pending) {
            if (accepted.has(e.eventId)) await this.db.events.update(e.eventId, { state: 1 });
            else if (rejected.has(e.eventId)) await this.db.events.update(e.eventId, { state: 2, rejectCode: rejected.get(e.eventId) });
          }
          for (const se of r.events) {
            if (await this.db.events.get(se.event_id)) continue;
            await this.db.events.put({
              eventId: se.event_id,
              childId: se.child_id,
              type: se.type,
              occurredAt: se.occurred_at,
              contentRelease: se.content_release ?? undefined,
              payload: se.payload,
              state: 3,
              serverSeq: se.server_seq,
            });
            if (se.type === "child_upserted") await this.applyChild(se);
          }
          await this.db.set("syncCursor", r.cursor);
          await this.db.set("lastSyncAt", Date.now());
        });
        sent += accepted.size;
        received += r.events.length;
        const more = r.has_more || (await this.db.events.where("state").equals(0).count()) > 0;
        if (!more) break;
      }
      if (sent || received) this.onChange();
      return { sent, received, offline: false };
    } catch (e) {
      if (e instanceof OfflineError) return { sent, received, offline: true };
      if (e instanceof ApiError && e.code === "auth.account_gone") {
        // Аккаунт удалён или слит на другом устройстве — начать заново анонимно.
        await this.db.del("tokens");
        await this.db.set("syncCursor", 0);
      }
      throw e;
    }
  }

  private async applyChild(se: ServerEvent) {
    const p = se.payload;
    const existing = await this.db.children.get(se.child_id);
    const at = Date.parse(se.occurred_at);
    if (existing && existing.updatedAt > at) return;
    const child: Child = {
      id: se.child_id,
      nickname: String(p.nickname ?? ""),
      birthYear: Number(p.birth_year ?? 2020),
      stage: (p.stage as Stage) ?? "A",
      formatCode: String(p.format_code ?? "1v1"),
      updatedAt: at,
    };
    await this.db.children.put(child);
  }

  /**
   * После входа по email аккаунт устройства мог смениться (слияние). Курсор прежнего аккаунта не подходит
   * новому — события существующего аккаунта с меньшими номерами иначе не дошли бы до этого устройства.
   */
  async resetCursor(): Promise<void> {
    await this.db.set("syncCursor", 0);
  }

  /** Когда синхронизироваться (API.md §3.4): выход в сеть, сворачивание, раз в 5 минут. */
  start(): () => void {
    const kick = () => void this.syncNow().catch(() => {});
    const onVis = () => {
      if (document.visibilityState === "hidden") kick();
    };
    window.addEventListener("online", kick);
    document.addEventListener("visibilitychange", onVis);
    this.timer = setInterval(kick, 5 * 60 * 1000);
    kick();
    return () => {
      window.removeEventListener("online", kick);
      document.removeEventListener("visibilitychange", onVis);
      if (this.timer) clearInterval(this.timer);
    };
  }
}
